/**
 * Google's JobPosting guidance calls `description` "the full description of the job in HTML
 * format" and says it recognizes `<p>`, `<ul>`, `<li>` (a bare `\n` is only its stated *minimum*
 * fallback). Our crawled/LLM-extracted description text is plain text with single newlines
 * separating lines or bullets — never actual markup — so this wraps it in real `<p>` tags
 * instead of relying on the bare-\n minimum.
 */

const ALREADY_HTML_MARKUP = /<\s*(p|div|ul|ol|li|br)\b/i;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function formatJobPostingDescriptionHtml(raw: string): string {
  // Already real markup (some ATS-sourced descriptions carry it through the crawl) — don't
  // double-wrap or escape content that's meant to render as-is.
  if (ALREADY_HTML_MARKUP.test(raw)) return raw;

  const normalized = raw.replace(/\r\n/g, '\n');
  // Blank-line-separated paragraphs when present; otherwise treat every non-empty line as its
  // own paragraph, since our source text is far more often single-newline-per-line/bullet than
  // blank-line-separated prose.
  const blocks = normalized.includes('\n\n') ? normalized.split(/\n{2,}/) : normalized.split('\n');

  return blocks
    .map((block) => escapeHtml(block.trim()))
    .filter((block) => block.length > 0)
    .map((block) => `<p>${block}</p>`)
    .join('');
}
